import { StateCreator, StoreApi } from 'zustand/vanilla';
import { Inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { AuthResult, AzureAuthService } from '../../azure/azure-auth.service';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';

export interface Token {
  token: string;
  expiresOn: number;
}

export interface Tokens {
  accessToken: Token;
  refreshToken: Token;
}

interface AuthorizationRoles {
  rollen: string[];
}

export interface AuthState {
  loggedIn: boolean;
  email: string | null;
  name: string | null;
  surname: string | null;
  displayName: string | null;
  redirectState: string | null;
  avsApi: Tokens | null;
  roles: string[];
}

export interface AuthSlice {
  auth: AuthState;
}

export interface AuthActions {
  redirectToLogIn: () => void;
  authenticate: (callbackParams: any) => Promise<void>;
  authorize: () => Promise<void>;
  refreshTokens: () => Promise<string | null>;
  logOut: () => void;
  reset: () => void;
  handleFailedAuth: () => void;
}

const initialAuthState: AuthState = {
  loggedIn: false,
  email: null,
  name: null,
  surname: null,
  displayName: null,
  redirectState: null,
  avsApi: null,
  roles: [],
};

@Injectable()
export class AuthStoreService {
  private slice: StateCreator<AuthSlice, [], [], AuthSlice>;
  private actions: (store: StoreApi<AuthSlice>) => AuthActions;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private azureAuth: AzureAuthService,
    private http: HttpClient,
    private router: Router,
    private message: NzMessageService,
  ) {
    this.slice = () => ({
      auth: initialAuthState,
    });

    this.actions = (store) => ({
      redirectToLogIn() {
        const loggedIn = store.getState().auth.loggedIn;
        if (!loggedIn) {
          const redirectState = (
            DateTime.now().toUnixInteger() * Math.random()
          ).toString();
          store.setState((state) => ({
            auth: { ...state.auth, redirectState },
          }));

          void azureAuth.logIn(redirectState);
        }
      },

      async authenticate(callbackParams) {
        try {
          const {
            auth: { redirectState },
          } = store.getState();

          if (!redirectState) {
            return;
          }
          const authResult = await azureAuth.handleAuthCodeFlow(
            redirectState,
            callbackParams,
          );

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const parsedIdToken = jwtDecode(authResult.id_token) as any;

          store.setState((state) => ({
            auth: {
              ...state.auth,
              loggedIn: true,
              redirectState: null,
              email: extractEmail(parsedIdToken),
              displayName: parsedIdToken.name,
              name: parsedIdToken.given_name,
              surname: parsedIdToken.family_name,
              avsApi: authResultToTokens(authResult),
              // roles: roles.rollen,
              // idToken: {
              //   raw: idToken,
              //   parsed: parsedIdToken,
              // },
            },
          }));
        } catch (error) {
          // TODO: handle error
          // console.log('ERROR', error);
        }
      },

      async authorize(): Promise<void> {
        const roles = await firstValueFrom(
          http.get<AuthorizationRoles>(`${environment.api.avs}/authorize`, {}),
        );
        store.setState((state) => ({
          auth: {
            ...state.auth,
            roles: roles.rollen,
          },
        }));
      },

      logOut() {
        this.reset();

        azureAuth.logOut();
      },

      reset() {
        store.setState({ auth: initialAuthState });
      },

      handleFailedAuth() {
        message.error(
          'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
        );
        this.reset();
        router.navigate(['/auth']);
      },

      async refreshTokens() {
        const state = store.getState().auth;
        const refreshToken = state.avsApi?.refreshToken;

        if (!state.avsApi || !refreshToken) {
          this.handleFailedAuth();
          return null;
        }

        try {
          const authResult = await azureAuth.acquireTokenSilent(
            refreshToken.token,
          );

          store.setState((state) => ({
            auth: {
              ...state.auth,
              avsApi: authResultToTokens(authResult),
            },
          }));

          return authResult.access_token;
        } catch (error) {
          console.error('Failed to refresh token:', error);
          this.handleFailedAuth();
          return null;
        }
      },
    });
  }

  getSlice() {
    return this.slice;
  }

  getActions() {
    return this.actions;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractEmail(claims: any) {
  return claims.email ?? claims?.emails?.[0] ?? claims.preferred_username ?? '';
}

function authResultToTokens(authResult: AuthResult): Tokens {
  return {
    accessToken: {
      token: authResult.access_token,
      expiresOn: authResult.expires_on,
    },
    refreshToken: {
      token: authResult.refresh_token,
      expiresOn: DateTime.now()
        .plus({ seconds: authResult.refresh_token_expires_in })
        .toUnixInteger(),
    },
  };
}
